import React from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Music } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';

const Productions = () => {
  const location = useLocation();
  const currentLang = location.pathname.startsWith('/de') ? 'de' : 'en';

  const seoContent = {
    en: {
      title: 'Podcast Productions Zurich | Our Video Podcast Portfolio | Professional Studio',
      description: 'View our podcast productions from Zurich. Professional video podcasts including The Leadership Lab, Education to Go, Geld & Pfeffer and more. Full-service podcast production studio in Switzerland.',
    },
    de: {
      title: 'Podcast Produktionen Zürich | Unser Video Podcast Portfolio | Professionelles Studio',
      description: 'Entdecken Sie unsere Podcast-Produktionen aus Zürich. Professionelle Video-Podcasts wie The Leadership Lab, Education to Go, Geld & Pfeffer und mehr. Full-Service Podcast-Studio in der Schweiz.',
    }
  };

  const productionsStructuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": seoContent[currentLang].title,
    "description": seoContent[currentLang].description,
    "url": `https://podcastzurich.com/${currentLang}/productions`,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": 9,
      "itemListElement": [
        {
          "@type": "PodcastSeries",
          "name": "The Leadership Lab",
          "description": "Join Maurice Thornton as he hosts top leaders in pharma, biotech, medtech, and digital health."
        },
        {
          "@type": "PodcastSeries",
          "name": "Education to Go",
          "description": "Der monatliche Podcast von Margrit Stamm beleuchtet Trends aus dem Bildungsbereich."
        },
        {
          "@type": "PodcastSeries",
          "name": "Geld & Pfeffer",
          "description": "Ehrlich über Geld und Wirtschaft mit handfesten Tipps und spannenden Einblicken."
        }
      ]
    }
  };

  const content = {
    en: {
      title: 'Our Productions',
      subtitle: 'Podcasts we proudly produce at Podcast Zurich',
      listen: 'Listen on Spotify',
    },
    de: {
      title: 'Unsere Produktionen',
      subtitle: 'Podcasts, die wir stolz bei Podcast Zürich produzieren',
      listen: 'Auf Spotify anhören',
    },
  };

  const t = content[currentLang];

  // Real productions data with Spotify descriptions
  const productions = [
    {
      id: 1,
      title: 'Mateo Gudenrath Show',
      description: 'Comedian Mateo Gudenrath nimmt sich bei Selbstgesprächen auf. Manchmal hat er Gäste.',
      thumbnail: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/4kgx82fq_mateo%20gudenrath.png',
      spotifyUrl: 'https://open.spotify.com/show/3N8NyrR8pkYMDY3sZ5Ll1c?si=4ad5f957bae34962',
    },
    {
      id: 2,
      title: 'Education to Go',
      description: 'Der monatliche Podcast von Margrit Stamm beleuchtet Trends und Themen aus dem Bildungsbereich. Die Bildungsforscherin im Gespräch mit Dominic Dillier und verschiedenen Gästen.',
      thumbnail: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/kx6qzvnb_education%20to%20go.png',
      spotifyUrl: 'https://open.spotify.com/show/0VOgz33kqiZ7hW5OPcmOp8?si=dead8f9821994b3e',
    },
    {
      id: 3,
      title: 'Begegnungen @fondetudes',
      description: 'Der Podcast der Schweizerischen Studienstiftung. Gäste erzählen von ihren Leidenschaften und geben Einblicke in das Förderprogramm und Alumni-Netzwerk.',
      thumbnail: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/rwd86d4m_fondetudes.png',
      spotifyUrl: 'https://open.spotify.com/show/1BtLzuy5iXCn3pYkwH65QO?si=d76690cb8fb645eb',
    },
    {
      id: 4,
      title: 'The Leadership Lab',
      description: 'Join Maurice Thornton, founder and managing director of headcount AG, as he hosts top leaders and innovators in pharma, biotech, medtech, and digital health.',
      thumbnail: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/eoaxr0xk_the%20leadership%20lab.png',
      spotifyUrl: 'https://open.spotify.com/show/1qLEGRl5QS86e6ihIXpUqr?si=7946488a4abd42b9',
    },
    {
      id: 5,
      title: 'Geld & Pfeffer',
      description: 'Ehrlich, auf Schweizerdeutsch und mit einer Prise Pfeffer reden wir Klartext über Geld und Wirtschaft. Handfeste Tipps und spannende Einblicke mit Expertinnen und Experten.',
      thumbnail: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/rx5w99nj_geld%20pfeffer.jpg',
      spotifyUrl: 'https://open.spotify.com/show/1YQqTjgM4azbEUrAO3hkDK?si=a0a3fc8554804943',
    },
    {
      id: 6,
      title: 'Sow it Goes',
      description: 'Stories shape us. Conversations connect us. Ideas plant the seeds for change. That\'s what Sow It Goes is all about.',
      thumbnail: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/otrpxbkd_sow%20it%20goes.png',
      spotifyUrl: 'https://open.spotify.com/show/7yhxrQJOaiYfQsukbbePJ5?si=be5cc7785c3e4c5c',
    },
    {
      id: 7,
      title: 'Rock & Randale',
      description: 'Wie es mit Mann und Frau auf Augenhöhe klappen kann. Der Podcast soll Lust machen auf Veränderung – in der Gesellschaft und bei jedem selbst.',
      thumbnail: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/ld7lowys_Screenshot%202025-11-21%20at%2014.56.50.png',
      spotifyUrl: 'https://open.spotify.com/show/1ncur8b2CpyFz3gNWeTHxJ?si=b570a43b8aa8495d',
    },
    {
      id: 8,
      title: 'Coumba Sow\'s Weg zur UEFA Women\'s EURO 2025',
      description: 'Begleite Fussballerin Coumba Sow im Visa Schweiz Podcast auf ihrem Weg zum Heimturnier – echt, persönlich und ungeschminkt!',
      thumbnail: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/vx9trmy2_Visa%20podcast.png',
      spotifyUrl: 'https://open.spotify.com/show/6GfyUjFv5ZVZub619W1gSY?si=fead716598a94f5e',
    },
    {
      id: 9,
      title: 'DETOX. ENERGISE. PERFORM - by Chenot',
      description: 'A health podcast hosted by Dr. George Gaitanos focusing on improving healthspan through evidence-based insights on detox, nutrition, sleep and recovery.',
      thumbnail: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/7clx0qem_Chenot.png',
      spotifyUrl: 'https://open.spotify.com/show/1Eb13hMHMIFv6Ju5GZvkFj?si=3719ca1bcc4847b6',
    },
  ];

  return (
    <>
      <SEOHead 
        title={seoContent[currentLang].title}
        description={seoContent[currentLang].description}
        canonicalPath={`/${currentLang}/productions`}
        ogImage="https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/4kgx82fq_mateo%20gudenrath.png"
        structuredData={productionsStructuredData}
      />
      <main className="min-h-screen pt-32 pb-20 px-6 bg-gradient-to-br from-gray-100 to-gray-200" role="main">
        <div className="max-w-7xl mx-auto">
          <header className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{t.title}</h1>
            <p className="text-xl text-gray-600">{t.subtitle}</p>
          </header>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" role="list" aria-label="Podcast productions">
            {productions.map((production) => (
              <Card key={production.id} className="overflow-hidden hover:shadow-xl transition-shadow" role="listitem">
                <figure className="aspect-square overflow-hidden bg-gray-200">
                  <img
                    src={production.thumbnail}
                    alt={`${production.title} - Podcast produced by Podcast Zurich`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    width="400"
                    height="400"
                  />
                </figure>
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold mb-3">{production.title}</h2>
                  <p className="text-gray-600 mb-6">{production.description}</p>
                  
                  <div className="flex flex-wrap gap-3">
                    {production.spotifyUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                        onClick={() => window.open(production.spotifyUrl, '_blank')}
                        aria-label={`Listen to ${production.title} on Spotify`}
                        data-testid={`spotify-btn-${production.id}`}
                      >
                        <Music className="w-4 h-4" aria-hidden="true" />
                        {t.listen}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </main>
    </>
  );
};

export default Productions;